// Signed Anonymous Identity — client helper (Plan v3.1 §2, §9).
//
// Feature-flagged: when anon-init returns 503 (server flag OFF) this module
// silently no-ops and the app continues on the legacy IP-hash path. When the
// server flag is ON, ensureAnonToken() lazily mints a token, caches it in
// localStorage, and rotates it when analyze-hair sends x-hpe-anon-refresh.
//
// SECURITY: This token is identity-only. It cannot grant paid access or
// account access. See plan §7a threat model — localStorage is not a trust
// boundary; the blast radius is bounded to "which guest bucket".

const STORAGE_KEY = "hpe_anon_v1";
const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

interface StoredToken {
  token: string;
  anon_id: string;
  exp: number;
}

function read(): StoredToken | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredToken;
    if (!parsed?.token || !parsed?.anon_id || typeof parsed.exp !== "number") return null;
    if (parsed.exp * 1000 < Date.now()) return null; // expired
    return parsed;
  } catch { return null; }
}

function write(t: StoredToken) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(t)); } catch { /* ignore */ }
}

export function clearAnonToken() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

let inflight: Promise<StoredToken | null> | null = null;

/**
 * Returns the current anon token, minting one if needed. Returns null when the
 * server-side feature flag is OFF (503 anon_token_disabled) — callers then
 * simply omit the X-HPE-Anon header and the edge functions fall back to the
 * legacy IP-hash path.
 */
export async function ensureAnonToken(): Promise<StoredToken | null> {
  const existing = read();
  if (existing) return existing;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const resp = await fetch(`https://${PROJECT_ID}.supabase.co/functions/v1/anon-init`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: "{}",
      });
      if (resp.status === 503) return null; // flag OFF — silent fallback
      if (!resp.ok) return null;
      const body = await resp.json();
      if (!body?.token || !body?.anon_id || !body?.exp) return null;
      const stored: StoredToken = { token: body.token, anon_id: body.anon_id, exp: body.exp };
      write(stored);
      return stored;
    } catch (e) {
      console.warn("[anonIdentity] mint failed (fallback to legacy):", e);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Merge X-HPE-Anon into a request headers object when we have a token.
 * Safe to call unconditionally — no-ops when flag is OFF.
 */
export async function withAnonHeader(headers: Record<string, string> = {}): Promise<Record<string, string>> {
  const t = await ensureAnonToken();
  if (!t) return headers;
  return { ...headers, "x-hpe-anon": t.token };
}

/**
 * Called after any function response to rotate the token if the server issued
 * a fresh one via `x-hpe-anon-refresh` (near-expiry sliding window).
 */
export function handleRefreshHeader(response: Response) {
  const refreshed = response.headers.get("x-hpe-anon-refresh");
  if (!refreshed) return;
  const existing = read();
  if (!existing) return;
  // We don't decode the JWT client-side — trust the server's fresh token and
  // reuse the existing anon_id/exp is not valid, so we do a lightweight decode
  // of the payload segment to read exp.
  try {
    const [, payloadB64] = refreshed.split(".");
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    write({ token: refreshed, anon_id: payload.sub, exp: payload.exp });
  } catch { /* ignore malformed refresh */ }
}

/**
 * Rotate the token from a JSON response body when the server emits it via
 * `_meta.anon_refresh` (used because supabase.functions.invoke does not
 * expose response headers on success).
 */
export function applyRefreshFromBody(body: unknown) {
  const t = (body as { _meta?: { anon_refresh?: string } } | null)?._meta?.anon_refresh;
  if (typeof t !== "string" || !t) return;
  try {
    const [, payloadB64] = t.split(".");
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    write({ token: t, anon_id: payload.sub, exp: payload.exp });
  } catch { /* ignore */ }
}

/**
 * Bind this anon identity to the freshly-signed-in user. Call once after a
 * successful signup/login while the anon token still exists in storage.
 */
export async function claimAnonIdentity(supabaseAccessToken: string): Promise<boolean> {
  const existing = read();
  if (!existing) return false;
  try {
    const resp = await fetch(`https://${PROJECT_ID}.supabase.co/functions/v1/anon-claim`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${supabaseAccessToken}`,
        "x-hpe-anon": existing.token,
      },
      body: "{}",
    });
    if (resp.status === 503) return false; // flag OFF
    if (resp.ok) { clearAnonToken(); return true; }
    return false;
  } catch (e) {
    console.warn("[anonIdentity] claim failed:", e);
    return false;
  }
}
